import { type ReactNode, type RefObject, useRef } from "react";

import { useGridMetrics, useVirtualRows } from "@/hooks/useVirtualRows";
import { rowCount } from "@/lib/virtualRows";

import type { FileManagerItem } from "./FileManagerList";

/**
 * Quantos itens desenhar antes da primeira medição: o bastante para encher
 * qualquer tela e dar à grade um card para medir. A janela de verdade vem no
 * mesmo quadro, pelo `useLayoutEffect`.
 */
const ITEMS_BEFORE_MEASURE = 48;

interface FileManagerGridProps {
  items: FileManagerItem[];
  scrollRef: RefObject<HTMLElement>;
  resetKey: string;
  renderItem: (item: FileManagerItem) => ReactNode;
}

/**
 * A grade do gerenciador com janela virtual (A1).
 *
 * As classes da grade são as de sempre — colunas por breakpoint, `gap-3`,
 * cards quadrados; o que muda é que só as linhas perto do viewport existem
 * no DOM, e o resto vira `padding` do tamanho exato, para a barra de rolagem
 * e a posição continuarem as mesmas. Medido antes (16/10/2026): 306 ms para
 * entrar numa pasta com 3.000 subpastas e 880 ms com 10.000; com a janela o
 * custo deixa de depender de quantas pastas a pasta tem.
 */
export function FileManagerGrid({ items, scrollRef, resetKey, renderItem }: FileManagerGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const metrics = useGridMetrics(gridRef, items.length);
  const segments = metrics
    ? [{ count: rowCount(items.length, metrics.columns), height: metrics.rowHeight, gap: metrics.gap }]
    : [];
  const window = useVirtualRows({ scrollRef, containerRef: gridRef, segments, overscan: 2, resetKey });

  let start = 0;
  let end = Math.min(items.length, ITEMS_BEFORE_MEASURE);
  let before = 0;
  let after = 0;
  if (metrics && window.rows > 0) {
    start = window.first * metrics.columns;
    end = Math.min(items.length, (window.last + 1) * metrics.columns);
    before = window.before;
    after = window.after;
  }

  return (
    <div
      ref={gridRef}
      className="grid grid-cols-2 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
      style={{ paddingTop: before, paddingBottom: after }}
    >
      {items.slice(start, end).map(renderItem)}
    </div>
  );
}
